import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';

import {FooterLink, H4} from 'UI/components/layout/Text';

import {getHrefForLinkType, getRelAttributes, getTargetAttribute} from 'Application/utils/linkFieldUtils';
import {isDefined} from 'Application/utils/typeGuards';

import type {FooterColumn} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `LinksColumn` component.
 * This interface specifies the required footer column data and optional test identifier for proper component rendering and testing.
 * It ensures type safety by defining the expected structure of props passed to the LinksColumn component.
 */
interface ComponentProps {
    /**
     * The footer column data containing headline and links array for rendering the navigation column.
     * This property includes the column's headline text and an array of link objects with their respective URLs and labels.
     * The column data is processed to generate clickable footer links with appropriate navigation attributes.
     */
    column: FooterColumn;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'LinksColumn'
     */
    testId?: string;
}

/**
 * The LinksColumn component renders a navigation column with a headline and a list of footer links.
 * It processes the provided footer column data to display clickable links with appropriate attributes for external links, internal navigation, and accessibility.
 * The component handles undefined or missing links gracefully and provides proper ARIA labeling for screen readers.
 *
 * @param props        The component props.
 * @param props.column The footer column data containing headline and links array.
 * @param props.testId The test identifier for component testing purposes.
 * @returns The rendered navigation column with headline and footer links.
 *
 * @example
 * ```tsx
 * <LinksColumn
 *     column={{
 *         headline: 'Quick Links',
 *         links: [
 *             { id: '1', link: { label: 'About Us', url: '/about' } },
 *             { id: '2', link: { label: 'Contact', url: '/contact' } }
 *         ]
 *     }}
 *     testId="footer-links-column"
 * />
 * ```
 */
const LinksColumn = ({column, testId = 'LinksColumn'}: ComponentProps) => (
    <Wrapper aria-label="Footer Links" data-cy={testId}>
        <Spacer y={6} />
        <H4>{column.headline}</H4>
        <Spacer y={4} />
        <List>
            {column.links?.map(link => {
                if (isDefined(link.link)) {
                    const href = getHrefForLinkType(link.link);
                    const {label} = link.link;
                    const rel = getRelAttributes(link.link) ?? '';
                    const target = getTargetAttribute(link.link) ?? '';

                    return (
                        <li key={link.id}>
                            <FooterLink href={href} rel={rel} target={target}>{label}</FooterLink>
                        </li>
                    );
                }

                return null;
            })}
        </List>
    </Wrapper>
);

LinksColumn.displayName = 'LinksColumn';

export {LinksColumn};

const Wrapper = styled.nav`
    padding-inline-end: ${spacing(4)};
`;

const List = styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    list-style: none;
    margin: 0;
    padding: 0;
`;